/**
 * Reader Font Context
 *
 * Manages the user's preferred reading font for the journal reader and editor.
 * The selected font is applied to both surfaces so the experience is consistent
 * whether the user is writing or reading.
 *
 * Preference is persisted to localStorage under the key 'vellum:readerFont'.
 * Falls back to DEFAULT_READER_FONT_ID ('bookerly') if no valid value is stored.
 *
 * Usage:
 *   const { readerFontId, readerFont, setReaderFont } = useReaderFont();
 *   // readerFont.fontFamily — full CSS stack ready to apply
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  readerFonts,
  getReaderFont,
  DEFAULT_READER_FONT_ID,
  type ReaderFont,
  type ReaderFontId,
} from '@codex/theme';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ReaderFontContextValue {
  /** The stored font ID */
  readerFontId: ReaderFontId;
  /** The full font object — use readerFont.fontFamily to apply the CSS stack */
  readerFont: ReaderFont;
  /** Update and persist the selected font */
  setReaderFont: (id: ReaderFontId) => void;
}

// ── Context ───────────────────────────────────────────────────────────────────

const ReaderFontContext = createContext<ReaderFontContextValue | null>(null);

const STORAGE_KEY = 'vellum:readerFont';

function readStoredFont(): ReaderFontId {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && readerFonts.some((f) => f.id === stored)) {
      return stored as ReaderFontId;
    }
  } catch {
    // localStorage may be blocked (private browsing, etc.)
  }
  return DEFAULT_READER_FONT_ID;
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function ReaderFontProvider({ children }: { children: React.ReactNode }) {
  const [readerFontId, setReaderFontId] = useState<ReaderFontId>(readStoredFont);

  const setReaderFont = useCallback((id: ReaderFontId) => {
    setReaderFontId(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // ignore storage errors
    }
  }, []);

  const readerFont = useMemo(() => getReaderFont(readerFontId), [readerFontId]);

  const value = useMemo(
    () => ({ readerFontId, readerFont, setReaderFont }),
    [readerFontId, readerFont, setReaderFont],
  );

  return <ReaderFontContext.Provider value={value}>{children}</ReaderFontContext.Provider>;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useReaderFont(): ReaderFontContextValue {
  const ctx = useContext(ReaderFontContext);
  if (!ctx) throw new Error('useReaderFont must be used within a <ReaderFontProvider>');
  return ctx;
}
